<script setup lang="ts">
import {
    faCheck,
    faGaugeMin,
    faCalendarClock,
    faListOl,
    faReceipt,
    faUserLock,
    faMoneyBillTransfer,
    faMouse,
} from '@fortawesome/pro-regular-svg-icons';
import { faPhone } from '@fortawesome/pro-solid-svg-icons';
import InformationContainer from '@components/ui/auto-reserve&gift-card&index/InformationContainer.vue';
import { OrganizationDetailDocument } from '@schema/directus';
import Regex from '@static/regex';

const contentStore = useContentStore();
const route = useRoute();

const layoutStore = useLayoutStore();
const { organizationalSupportNumber } = classToRefs(layoutStore);

const callNumber = ref('');
const name = ref('');
const organizationName = ref('');
const giftCardCount = ref<number>();
const giftCardCredit = ref<number>();

const form = new Form({
    callNumber: new Field(callNumber, {
        required: true,
        validator: value =>
            Regex.internationalPhoneNumber.full.test(
                convertPersianNumbers(value ?? '')
            ),
        partialValidator: value =>
            !value || Regex.internationalPhoneNumber.partial.test(value),
        maxLength: 16,
    }),
    name: new Field(name, {
        required: true,
        validator: value => Regex.name.full.test(value ?? ''),
    }),
    organizationName: new Field(organizationName, {
        required: true,
        validator: value => Regex.name.full.test(value ?? ''),
    }),
    giftCardCount: new Field(giftCardCount, {
        validator: value =>
            Regex.number.full.test(
                convertToEnglishDigit(value?.toString() ?? '')
            ),
        maxLength: 9,
    }),
    giftCardCredit: new Field(giftCardCredit, {
        validator: value =>
            Regex.number.full.test(
                convertToEnglishDigit(value?.toString() ?? '')
            ),
        maxLength: 15,
    }),
});

const { mutate } = useMutation(OrganizationDetailDocument);
const addOrganization = async () => {
    if (!form.validateAll()) {
        nextTick(() => {
            navToSection();
        });
        return;
    }
    try {
        await mutate({
            Name: name.value.trim(),
            CallNumber: callNumber.value.trim(),
            OrganizationName: organizationName.value.trim(),
            Source: 'GiftCardLanding',
            Description: '',
            GiftCardCount: giftCardCount.value
                ? +giftCardCount.value
                : undefined,
            GiftCardCredit: giftCardCredit.value?.toString() || undefined,
        });
        form.reset();
        swal({
            icon: 'success',
            text: 'درخواست شما با موفقیت ثبت شد.',
            confirmButtonText: 'باشه',
        });
    } catch (e) {
        await handleException({
            message:
                'درخواست شما ثبت شده است؛ همکاران ما با شما تماس خواهند گرفت.',
        });
        reportException(e);
    }
};
useAsyncData(async () => {
    await contentStore.getPageData(route.path);
    return true;
});
useServerHead({
    htmlAttrs: {
        class: 'smooth-scroll',
    },
});
useServerHead({
    title: contentStore.landingPageTitle,
});
useSeoMeta({
    description: contentStore.landingPageDescription,
    ogTitle: contentStore.landingPageTitle,
    ogDescription: contentStore.landingPageDescription,
});
definePageMeta({
    layout: 'landing',
});

const bannerItems = [
    'به عنوان رفاهیات کارکنان',
    'به عنوان پاداش عملکرد و تشویقی',
    'به عنوان هدیه مناسبتی',
];

const services = [
    {
        id: 'tab-hotel',
        label: 'هتل',
        href: '/hotel',
        desktopIcon:
            '<path d="M36.8428 19.9652V19.2542C36.8428 17.9444 36.1647 16.7906 35.1417 16.1234V8.36744C35.1417 6.32679 33.4815 4.66663 31.4409 4.66663H8.55654C6.5159 4.66663 4.85566 6.32679 4.85566 8.36744V16.1234C3.83264 16.7906 3.15451 17.9443 3.15451 19.2541V19.9651C2.10699 20.241 1.33203 21.1962 1.33203 22.3292V30.6487C1.33203 31.9575 2.36614 33.0292 3.66019 33.09V34.0746C3.66019 34.6775 4.14895 35.1663 4.75182 35.1663C5.3547 35.1663 5.84346 34.6775 5.84346 34.0746V33.093H34.1539V34.0746C34.1539 34.6775 34.6426 35.1663 35.2455 35.1663C35.8484 35.1663 36.3371 34.6775 36.3371 34.0746V33.09C37.6313 33.0291 38.6654 31.9574 38.6654 30.6486V22.3292C38.6654 21.1961 37.8904 20.241 36.8428 19.9652ZM7.039 8.36744C7.039 7.53065 7.71975 6.8499 8.55654 6.8499H31.4409C32.2776 6.8499 32.9584 7.53065 32.9584 8.36744V15.5182H31.5028V13.594C31.5028 12.2473 30.4072 11.1515 29.0603 11.1515H23.1863C21.8396 11.1515 20.7438 12.2473 20.7438 13.594V15.5182H19.2536V13.594C19.2536 12.2473 18.1578 11.1515 16.8111 11.1515H10.9371C9.59021 11.1515 8.49456 12.2473 8.49456 13.594V15.5182H7.039V8.36744ZM29.3195 13.594V15.2589C29.3195 15.4019 29.2032 15.5181 29.0603 15.5181H23.1863C23.0434 15.5181 22.9272 15.4018 22.9272 15.2589V13.594C22.9272 13.451 23.0435 13.3348 23.1863 13.3348H29.0603C29.2033 13.3348 29.3195 13.451 29.3195 13.594ZM17.0702 13.594V15.2589C17.0702 15.4019 16.9539 15.5181 16.8111 15.5181H10.9371C10.7941 15.5181 10.6779 15.4018 10.6779 15.2589V13.594C10.6779 13.451 10.7942 13.3348 10.9371 13.3348H16.8111C16.9539 13.3348 17.0702 13.451 17.0702 13.594ZM5.33785 19.2541C5.33785 18.3979 6.03443 17.7014 6.89061 17.7014H33.1068C33.963 17.7014 34.6595 18.398 34.6595 19.2541V19.8847H5.33785V19.2541ZM36.482 30.6486C36.482 30.7925 36.3649 30.9096 36.2209 30.9096H3.77642C3.63248 30.9096 3.5153 30.7925 3.5153 30.6486V22.3292C3.5153 22.1852 3.63241 22.0681 3.77642 22.0681H36.221C36.3649 22.0681 36.4821 22.1852 36.4821 22.3292V30.6486H36.482Z" />',
    },
    {
        id: 'tab-plane',
        label: 'پرواز داخلی',
        href: '/plane-ticket',
        desktopIcon:
            '<path fill-rule="evenodd" clip-rule="evenodd" d="M4.9909 25.1177C4.87846 25.0049 4.76602 24.768 4.64234 24.5423C4.52991 24.0798 4.64234 23.6285 4.86722 23.2788C5.21577 22.929 5.66552 22.5905 6.35139 22.3649C6.81238 22.2521 7.38581 22.1393 8.07168 22.0152C9.33098 21.9023 10.5903 22.0152 11.1637 22.0152H11.2761L17.1116 15.5844L6.72096 10.9663L5.67677 12.0306C5.32821 12.3803 4.64234 12.3803 4.30503 12.0306C3.95648 11.6808 3.84404 10.9926 4.30503 10.6542L8.19536 6.75059C8.65636 6.28803 9.34222 6.06239 10.0281 6.17521L12.884 6.73931L14.7167 4.90034C15.0653 4.5506 15.7512 4.5506 16.0885 4.90034C16.437 5.25008 16.5495 5.93829 16.0885 6.27675L15.1777 7.07777L18.2697 7.65315L20.2149 5.81419C20.4398 5.58854 20.7632 5.58855 20.9008 5.58854C21.2015 5.58853 21.3881 5.71253 21.5866 5.93829C21.9416 6.34204 22.0476 6.97623 21.5866 7.31469L20.6759 8.11572L24.3414 8.80392H24.5662C28.4566 4.21214 32.583 3.06137 34.8655 3.63676C35.0904 3.74958 35.3265 3.8624 35.5514 3.9865C35.7762 4.21214 35.8999 4.44906 35.8999 4.6747C36.3609 6.97623 35.3265 11.1055 30.7503 15.009V15.1219L31.4362 18.7998L32.3469 17.8859C32.6955 17.5362 33.3813 17.5362 33.7186 17.8859C34.0672 18.2357 34.1796 18.9239 33.7186 19.2624L31.7735 21.2141L32.3469 24.3167L33.2576 23.4029C33.6062 23.0531 34.2921 23.0531 34.6294 23.4029C34.9667 23.7526 35.0904 24.4408 34.6294 24.7793L32.7966 26.6182L33.3701 29.4951C33.4825 30.1833 33.2576 30.8716 32.7966 31.3341L28.9063 35.2377C28.5578 35.5874 27.8719 35.5874 27.5346 35.2377C27.186 34.8879 27.1041 34.2915 27.5346 33.8613L31.4249 29.9577L28.6814 15.4829C28.569 14.6819 28.7939 13.9936 29.3673 13.5311C33.7186 9.74033 33.9435 6.52495 33.9435 5.61111V5.49829H33.8311C32.9203 5.49829 29.7159 5.72393 25.938 10.0901C25.477 10.6655 24.6787 10.8911 23.8804 10.7783L9.5671 8.0029L8.08293 9.60494L18.6183 14.1967C18.8432 14.3095 18.9669 14.4224 19.0793 14.5465C19.6527 15.1219 19.6527 16.0357 19.1917 16.6111L12.6704 23.73C12.3218 24.0798 11.8721 24.1926 11.4111 24.1926C10.9501 24.1926 9.69078 24.0798 8.31904 24.0798C8.09417 24.0798 7.97049 24.0798 7.74561 24.1926H7.39706L12.3218 26.7198C12.4342 26.8326 12.5467 26.8326 12.6704 26.9454C12.7828 27.0582 12.8952 27.171 12.8952 27.2951L15.4138 32.2367L15.5263 31.7741C15.5263 31.5485 15.5263 31.4244 15.6387 31.1987C15.7512 29.9351 15.6387 28.6716 15.5263 28.0962C15.5263 27.6336 15.6387 27.1823 15.9873 26.8326L23.0821 20.289C23.6555 19.7136 24.5662 19.8265 25.1397 20.4018C25.2521 20.5147 25.3645 20.7516 25.4882 20.9772L27.6583 26.9454C27.7707 27.171 27.7989 27.467 27.6583 27.7464C27.529 28.0034 27.3582 28.1191 27.0848 28.209C26.6921 28.3381 26.3512 28.2758 26.0118 28.0398C25.8534 27.9297 25.7019 27.6336 25.7019 27.6336L23.8691 22.4664L17.5726 28.209V28.3218C17.6851 28.8972 17.6851 30.1608 17.5726 31.4244C17.5726 32.1126 17.4602 32.688 17.2241 33.1505C16.9992 33.8387 16.7631 34.3013 16.3133 34.6397C15.9648 34.9895 15.515 35.1023 15.054 34.8654C14.8292 34.7526 14.593 34.6397 14.4806 34.5156C14.3682 34.5156 14.3682 34.4028 14.2557 34.29L11.2761 28.4346L5.32821 25.3321C5.21577 25.2305 5.10334 25.2305 4.9909 25.1177Z" />',
    },
    {
        id: 'tab-international-plane',
        label: 'پرواز خارجی',

        href: '/international-flights',
        desktopIcon: `
                <path d="M4.64234 25.0423C4.76602 25.268 4.87846 25.5049 4.9909 25.6177C5.10334 25.7305 5.21577 25.7305 5.32821 25.8321L11.2761 28.9346L14.2557 34.79C14.3682 34.9028 14.3682 35.0157 14.4806 35.0157C14.593 35.1398 14.8292 35.2526 15.054 35.3654C15.515 35.6023 15.9648 35.4895 16.3133 35.1398C16.7631 34.8013 16.9992 34.3387 17.2241 33.6505C17.4602 33.188 17.5726 32.6126 17.5726 31.9244C17.6851 30.6608 17.6851 29.3972 17.5726 28.8218V28.709L20 25.2793L19.5347 24.0608L15.9873 27.3326C15.6387 27.6823 15.5263 28.1336 15.5263 28.5962C15.6387 29.1716 15.7512 30.4352 15.6387 31.6987C15.5263 31.9244 15.5263 32.0485 15.5263 32.2741L15.4138 32.7367L12.8952 27.7952C12.8952 27.6711 12.7828 27.5582 12.6704 27.4454C12.5467 27.3326 12.4343 27.3326 12.3218 27.2198L7.39706 24.6926H7.74561C7.97049 24.5798 8.09417 24.5798 8.31904 24.5798C9.69078 24.5798 10.9501 24.6926 11.4111 24.6926C11.8721 24.6926 12.3218 24.5798 12.6704 24.23L19.1917 17.1111C19.6527 16.5357 19.6527 15.6219 19.0793 15.0465C18.9669 14.9224 18.8432 14.8096 18.6183 14.6967L8.08293 10.1049L9.5671 8.5029L23.8804 11.2783C24.6787 11.3911 25.477 11.1655 25.938 10.5901C29.7159 6.22393 32.9203 5.99829 33.8311 5.99829H33.9435V6.11111C33.9435 7.02495 33.7186 10.2403 29.3673 14.0311C28.7939 14.4937 28.569 15.1819 28.6814 15.9829L31 26.5L31.4249 30.4577L31.7735 30.0781L32.7967 27.1182L34.6294 25.2793C35.0904 24.9408 34.9667 24.2526 34.6294 23.9029C34.2921 23.5531 33.6062 23.5531 33.2576 23.9029L32.3469 24.8167L31.7735 21.7141L33.7186 19.7624C34.1796 19.4239 34.0672 18.7357 33.7186 18.386C33.3813 18.0362 32.6955 18.0362 32.3469 18.386L31.4362 19.2998L30.7503 15.6219V15.509C35.3265 11.6055 36.3609 7.47623 35.8999 5.1747C35.8999 4.94906 35.7762 4.71214 35.5514 4.4865C35.3265 4.3624 35.0904 4.24958 34.8655 4.13676C32.583 3.56137 28.4566 4.71214 24.5662 9.30392H24.3414L20.6759 8.61572L21.5866 7.8147C22.0476 7.47623 21.9417 6.84205 21.5866 6.43829C21.3882 6.21253 21.2015 6.08853 20.9008 6.08855H20.9007C20.7631 6.08855 20.4398 6.08857 20.2149 6.31419L18.2697 8.15316L15.1777 7.57777L16.0885 6.77675C16.5495 6.43829 16.437 5.75008 16.0885 5.40034C15.7512 5.0506 15.0653 5.0506 14.7167 5.40034L12.884 7.23931L10.0281 6.67521C9.34222 6.56239 8.65636 6.78803 8.19536 7.25059L4.30503 11.1542C3.84404 11.4926 3.95648 12.1808 4.30503 12.5306C4.64234 12.8803 5.32821 12.8803 5.67677 12.5306L6.72096 11.4663L17.1116 16.0844L11.2761 22.5152H11.1637C10.5903 22.5152 9.33098 22.4024 8.07168 22.5152C7.38581 22.6393 6.81238 22.7521 6.35139 22.8649C5.66552 23.0906 5.21577 23.429 4.86722 23.7788C4.64234 24.1285 4.52991 24.5798 4.64234 25.0423Z" />
                <path fill="none" stroke="var(--color)" d="M32.125 28.5C32.125 32.1589 29.1589 35.125 25.5 35.125C21.8411 35.125 18.875 32.1589 18.875 28.5C18.875 24.8411 21.8411 21.875 25.5 21.875C29.1589 21.875 32.125 24.8411 32.125 28.5Z" stroke-width="1.75"/>
                <path fill="none" stroke="var(--color)" d="M26.5 22C30.0504 25.5504 30.0504 31.3067 26.5 34.8571M25.4286 22V34.8571M19 28.4286L31.8571 28.4286M24.3571 22C20.8067 25.5504 20.8067 31.3067 24.3571 34.8571" stroke-width="1.75"/>
            `,
    },
    {
        id: 'tab-train',
        label: 'قطار',
        href: '/train-ticket',
        desktopIcon: `
                <path d="M17.8283 8.70529H22.4845C23.0842 8.70529 23.5545 8.2232 23.5545 7.63528C23.5545 7.04737 22.9549 6.68286 22.3552 6.68286H17.6989C17.0992 6.68286 16.6289 7.16495 16.6289 7.75287C16.6289 8.34078 17.2286 8.70529 17.8283 8.70529Z" />
                <path d="M29.2928 33.0572C28.9401 32.7044 28.2228 32.7044 27.8583 33.0572C27.5056 33.4099 27.5056 34.1272 27.8583 34.4917L29.7632 36.3965C30.1159 36.7493 30.8332 36.7493 31.1977 36.3965C31.5504 36.0438 31.5504 35.3265 31.1977 34.962L29.2928 33.0572Z" />
                <path d="M11.1499 33.0585L9.23331 34.9634C8.88056 35.3161 8.88056 36.0334 9.23331 36.3979C9.58606 36.7624 10.3033 36.7506 10.6678 36.3979L12.5727 34.493C12.9254 34.1403 12.9254 33.423 12.5727 33.0585C12.2199 32.694 11.6202 32.694 11.1499 33.0585Z" />
                <path d="M35.1371 26.1314L32.6914 10.8573L32.515 10.1401L32.3974 9.50514C31.7977 5.91886 28.8111 3.33203 25.2249 3.33203H14.9716C11.3853 3.33203 8.36345 5.97765 7.76377 9.56393L7.68148 10.1401L7.5639 10.8573L5.05938 26.1314C4.57729 29.3531 7.09357 32.2222 10.3153 32.2222H29.8929C33.1029 32.2222 35.6192 29.3531 35.1371 26.1314ZM9.83325 9.89316C10.186 7.38864 12.4554 5.47204 14.9599 5.47204H25.2249C27.847 5.47204 29.9987 7.37689 30.3515 9.89316L30.4691 10.728C30.4691 10.728 30.4691 10.728 30.4691 10.8456C30.3515 11.1983 30.1163 11.5628 29.8694 12.1625C29.2697 13.2325 28.5525 14.667 27.6 16.1015C26.53 17.4185 25.3307 18.8412 24.0138 19.923C22.6968 20.993 21.3917 21.5927 20.0747 21.5927C18.7578 21.5927 17.335 20.993 16.1357 19.923C14.9364 18.853 13.6312 17.5361 12.6788 16.1015C11.6088 14.5494 10.8915 13.1149 10.2918 12.0449C10.0567 11.4453 9.80974 11.0925 9.69215 10.728C9.69215 10.728 9.69215 10.728 9.69215 10.6104L9.83325 9.89316ZM29.8812 30.0704H10.3153C8.4105 30.0704 6.8584 28.4007 7.21115 26.4841L9.116 14.1967C9.59809 15.1491 10.186 16.1015 10.9033 17.1833C11.9733 18.7354 13.2902 20.2875 14.7247 21.4751C16.2768 22.6744 18.0641 23.6269 19.9807 23.6269C22.0149 23.6269 23.8021 22.792 25.2366 21.4751C26.7887 20.2757 28.1056 18.7354 29.0581 17.1833C29.7753 16.1133 30.375 15.1491 30.8453 14.1967L32.7502 26.4841C33.3499 28.4007 31.7978 30.0704 29.8812 30.0704Z" />
                <path d="M28.3294 28.155C29.1866 28.155 29.8815 27.4601 29.8815 26.6029C29.8815 25.7457 29.1866 25.0508 28.3294 25.0508C27.4722 25.0508 26.7773 25.7457 26.7773 26.6029C26.7773 27.4601 27.4722 28.155 28.3294 28.155Z" />
                <path d="M11.8685 25.0508C11.0337 25.0508 10.3164 25.768 10.3164 26.6029C10.3164 27.4377 11.0337 28.155 11.8685 28.155C12.7033 28.155 13.4206 27.4377 13.4206 26.6029C13.4206 25.768 12.7033 25.0508 11.8685 25.0508Z" />
            `,
    },
    {
        id: 'tab-bus',
        label: 'اتوبوس',
        href: '/bus-ticket',
        desktopIcon: `
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10.4715 5.36009C9.40119 5.36009 8.56609 6.19725 8.56609 7.27024V7.38815C8.56609 8.34323 7.84862 9.06248 6.8959 9.06248C6.0608 9.06248 5.4727 9.66382 5.4727 10.4892V13.7082C5.4727 14.3095 4.96196 14.6632 4.40237 14.6632C3.84277 14.6632 3.33203 14.3095 3.33203 13.7082V10.6189C3.33203 8.82666 4.63761 7.28203 6.42542 7.15233C6.54304 5.00636 8.33086 3.33203 10.4715 3.33203H29.2906C31.4313 3.33203 33.2191 5.00636 33.3367 7.03442C35.2422 7.27024 36.6654 8.82666 36.6654 10.7368V13.7199C36.6654 14.3213 36.1949 14.7929 35.595 14.7929C34.9952 14.7929 34.5247 14.3213 34.5247 13.7199V10.7368C34.5247 9.89965 33.8072 9.18039 32.8545 9.18039C31.9018 9.18039 31.1843 8.46114 31.1843 7.50606V7.38815C31.1843 6.31516 30.3492 5.478 29.2789 5.478L10.4598 5.36009H10.4715Z" />
                <path fill-rule="evenodd" clip-rule="evenodd" d="M7.74242 10.8781C8.34228 10.8781 8.81276 11.3497 8.81276 11.951V28.1873C8.81276 29.8617 10.236 31.2884 11.9061 31.2884H28.22C29.8902 31.2884 31.3133 29.8617 31.3133 28.1873V11.951C31.3133 11.3497 31.7838 10.8781 32.3837 10.8781C32.9835 10.8781 33.454 11.3497 33.454 11.951V28.1873C33.454 31.0525 31.0781 33.3164 28.22 33.3164H11.9061C9.048 33.3164 6.67209 31.0525 6.67209 28.1873V11.951C6.66032 11.3497 7.14256 10.8781 7.74242 10.8781Z" />
                <path fill-rule="evenodd" clip-rule="evenodd" d="M10 9.2058C10 8.60445 10.4705 8.13281 11.0703 8.13281H28.9367C29.5366 8.13281 30.0071 8.60445 30.0071 9.2058C30.0071 9.80714 29.5366 10.2788 28.9367 10.2788H11.0703C10.4705 10.2788 10 9.80714 10 9.2058Z" />
                <path fill-rule="evenodd" clip-rule="evenodd" d="M27.1484 35.5925C27.1484 34.9912 27.6189 34.5195 28.2188 34.5195H30.4771C31.0769 34.5195 31.5474 34.9912 31.5474 35.5925C31.5474 36.1939 31.0769 36.6655 30.4771 36.6655H28.2188C27.5013 36.6655 27.1484 36.1821 27.1484 35.5925Z" />
                <path fill-rule="evenodd" clip-rule="evenodd" d="M8.56641 35.5925C8.56641 34.9912 9.03688 34.5195 9.63674 34.5195H11.895C12.4949 34.5195 12.9654 34.9912 12.9654 35.5925C12.9654 36.1939 12.4949 36.6655 11.895 36.6655H9.6485C9.04865 36.6655 8.56641 36.1821 8.56641 35.5925Z" />
                <path d="M29.7614 28.3053C29.7614 29.1425 29.0439 29.8618 28.2088 29.8618C27.3737 29.8618 26.6562 29.1425 26.6562 28.3053C26.6562 27.4682 27.3737 26.7489 28.2088 26.7489C29.0557 26.7489 29.7614 27.3502 29.7614 28.3053Z" />
                <path d="M13.3317 28.3053C13.3317 29.1425 12.6142 29.8618 11.7791 29.8618C10.944 29.8618 10.2266 29.1425 10.2266 28.3053C10.2266 27.4682 10.944 26.7489 11.7791 26.7489C12.6142 26.7489 13.3317 27.3502 13.3317 28.3053Z" />
            `,
    },
    {
        id: 'tab-taxi',
        label: 'سواری',
        href: '/taxi',
        desktopIcon: `
                <path d="M5.19726 18.64L4.26752 18.2717H4.26752L5.19726 18.64ZM6.10573 16.3462L7.03547 16.7144C7.15743 16.4064 7.119 16.0581 6.93284 15.7841C6.74668 15.5102 6.43695 15.3462 6.10573 15.3462V16.3462ZM3.5 14.7858C3.5 14.2336 3.05228 13.7858 2.5 13.7858C1.94772 13.7858 1.5 14.2336 1.5 14.7858H3.5ZM6.68462 12.682C6.46758 13.1899 6.70334 13.7775 7.2112 13.9946C7.71905 14.2116 8.30669 13.9758 8.52372 13.468L6.68462 12.682ZM9.49839 8.64251L8.57884 8.24954V8.24954L9.49839 8.64251ZM20 16.3462L19.9532 17.3451L20 17.3472L20.0468 17.3451L20 16.3462ZM10.0056 13.9595C9.49854 13.7406 8.91007 13.9743 8.69123 14.4814C8.47239 14.9885 8.70605 15.5769 9.21313 15.7958L10.0056 13.9595ZM11.8844 33.5C12.4367 33.5 12.8844 33.0523 12.8844 32.5C12.8844 31.9477 12.4367 31.5 11.8844 31.5V33.5ZM8.15104 31.5C7.59876 31.5 7.15104 31.9477 7.15104 32.5C7.15104 33.0523 7.59876 33.5 8.15104 33.5V31.5ZM34.8027 18.64L35.7325 18.2717V18.2717L34.8027 18.64ZM33.8943 16.3462V15.3462C33.5631 15.3462 33.2533 15.5102 33.0672 15.7841C32.881 16.0581 32.8426 16.4064 32.9645 16.7144L33.8943 16.3462ZM38.5 14.7858C38.5 14.2336 38.0523 13.7858 37.5 13.7858C36.9477 13.7858 36.5 14.2336 36.5 14.7858H38.5ZM31.4763 13.468C31.6933 13.9758 32.281 14.2116 32.7888 13.9946C33.2967 13.7775 33.5324 13.1899 33.3154 12.682L31.4763 13.468ZM30.5016 8.64251L31.4212 8.24954L31.4212 8.24954L30.5016 8.64251ZM30.7869 15.7958C31.2939 15.5769 31.5276 14.9885 31.3088 14.4814C31.0899 13.9743 30.5015 13.7406 29.9944 13.9595L30.7869 15.7958ZM28.5677 31.5C28.0154 31.5 27.5677 31.9477 27.5677 32.5C27.5677 33.0523 28.0154 33.5 28.5677 33.5V31.5ZM31.849 33.5C32.4012 33.5 32.849 33.0523 32.849 32.5C32.849 31.9477 32.4012 31.5 31.849 31.5V33.5ZM20 28.3794H8.80833V30.3794H20V28.3794ZM5.86979 25.438V20.3561H3.86979V25.438H5.86979ZM6.12699 19.0082L7.03547 16.7144L5.17599 15.9779L4.26752 18.2717L6.12699 19.0082ZM6.10573 15.3462H3.28827V17.3462H6.10573V15.3462ZM3.5 15.5579V14.7858H1.5V15.5579H3.5ZM8.52372 13.468L10.4179 9.03548L8.57884 8.24954L6.68462 12.682L8.52372 13.468ZM13.1216 7.25H20V5.25H13.1216V7.25ZM20 16.3462C20.0468 15.3472 20.0468 15.3473 20.0468 15.3473C20.0468 15.3472 20.0468 15.3472 20.0467 15.3472C20.0465 15.3472 20.0462 15.3472 20.0458 15.3472C20.0451 15.3472 20.0438 15.3471 20.0421 15.347C20.0386 15.3469 20.0333 15.3466 20.0262 15.3463C20.012 15.3456 19.9905 15.3445 19.9622 15.3431C19.9057 15.3402 19.8217 15.3358 19.7137 15.3298C19.4976 15.3178 19.1851 15.2993 18.8018 15.2738C18.0347 15.2227 16.9869 15.1432 15.8619 15.0299C14.7349 14.9165 13.5416 14.7701 12.4801 14.5864C11.3972 14.399 10.5281 14.185 10.0056 13.9595L9.21313 15.7958C9.96668 16.121 11.0344 16.3659 12.139 16.5571C13.2651 16.752 14.5099 16.9039 15.6615 17.0199C16.8152 17.1361 17.8865 17.2172 18.6689 17.2694C19.0603 17.2955 19.3802 17.3143 19.6027 17.3267C19.714 17.3329 19.801 17.3374 19.8604 17.3405C19.8902 17.342 19.9131 17.3431 19.9286 17.3439C19.9364 17.3443 19.9424 17.3445 19.9465 17.3447C19.9486 17.3448 19.9502 17.3449 19.9513 17.345C19.9519 17.345 19.9523 17.345 19.9526 17.345C19.9528 17.345 19.9529 17.345 19.953 17.345C19.9531 17.3451 19.9532 17.3451 20 16.3462ZM11.8844 31.5H8.15104V33.5H11.8844V31.5ZM20 30.3794H31.1917V28.3794H20V30.3794ZM36.1302 25.438V20.3561H34.1302V25.438H36.1302ZM35.7325 18.2717L34.824 15.9779L32.9645 16.7144L33.873 19.0082L35.7325 18.2717ZM33.8943 17.3462H36.7117V15.3462H33.8943V17.3462ZM38.5 15.5579V14.7858H36.5V15.5579H38.5ZM33.3154 12.682L31.4212 8.24954L29.5821 9.03548L31.4763 13.468L33.3154 12.682ZM26.8784 5.25H20V7.25H26.8784V5.25ZM20 16.3462C20.0468 17.3451 20.0469 17.3451 20.047 17.345C20.0471 17.345 20.0472 17.345 20.0474 17.345C20.0477 17.345 20.0481 17.345 20.0487 17.345C20.0498 17.3449 20.0514 17.3448 20.0535 17.3447C20.0576 17.3445 20.0636 17.3443 20.0714 17.3439C20.0869 17.3431 20.1098 17.342 20.1396 17.3405C20.199 17.3374 20.286 17.3329 20.3973 17.3267C20.6198 17.3143 20.9397 17.2955 21.3311 17.2694C22.1135 17.2172 23.1848 17.1361 24.3385 17.0199C25.4901 16.9039 26.7349 16.752 27.861 16.5571C28.9656 16.3659 30.0333 16.121 30.7869 15.7958L29.9944 13.9595C29.4719 14.185 28.6028 14.399 27.5199 14.5864C26.4584 14.7701 25.2651 14.9165 24.1381 15.0299C23.0131 15.1432 21.9653 15.2227 21.1982 15.2738C20.8149 15.2993 20.5024 15.3178 20.2863 15.3298C20.1783 15.3358 20.0943 15.3402 20.0378 15.3431C20.0095 15.3445 19.988 15.3456 19.9738 15.3463C19.9667 15.3466 19.9614 15.3469 19.9579 15.347C19.9562 15.3471 19.9549 15.3472 19.9542 15.3472C19.9538 15.3472 19.9535 15.3472 19.9533 15.3472C19.9532 15.3472 19.9532 15.3472 19.9532 15.3473C19.9532 15.3473 19.9532 15.3472 20 16.3462ZM28.5677 33.5H31.849V31.5H28.5677V33.5ZM31.4212 8.24954C30.6438 6.43046 28.8572 5.25 26.8784 5.25V7.25C28.0558 7.25 29.1191 7.95222 29.5821 9.03548L31.4212 8.24954ZM36.7117 17.3462C37.6994 17.3462 38.5 16.5455 38.5 15.5579H36.5C36.5 15.441 36.5948 15.3462 36.7117 15.3462V17.3462ZM36.1302 20.3561C36.1302 19.6424 35.9953 18.9352 35.7325 18.2717L33.873 19.0082C34.0429 19.4373 34.1302 19.8946 34.1302 20.3561H36.1302ZM5.86979 20.3561C5.86979 19.8946 5.95706 19.4373 6.12699 19.0082L4.26752 18.2717C4.00474 18.9352 3.86979 19.6424 3.86979 20.3561H5.86979ZM31.1917 30.3794C33.9216 30.3794 36.1302 28.1661 36.1302 25.438H34.1302C34.1302 27.0634 32.8152 28.3794 31.1917 28.3794V30.3794ZM3.28827 15.3462C3.4052 15.3462 3.5 15.4409 3.5 15.5579H1.5C1.5 16.5455 2.30064 17.3462 3.28827 17.3462V15.3462ZM10.4179 9.03548C10.8809 7.95222 11.9442 7.25 13.1216 7.25V5.25C11.1428 5.25 9.35623 6.43046 8.57884 8.24954L10.4179 9.03548ZM8.80833 28.3794C7.1848 28.3794 5.86979 27.0634 5.86979 25.438H3.86979C3.86979 28.1661 6.07837 30.3794 8.80833 30.3794V28.3794Z" />
                <path d="M12.8917 23.5094C12.8917 24.5242 12.0747 25.3469 11.0669 25.3469C10.0592 25.3469 9.24219 24.5242 9.24219 23.5094C9.24219 22.4946 10.0592 21.6719 11.0669 21.6719C12.0747 21.6719 12.8917 22.4946 12.8917 23.5094Z" />
                <path d="M27.0011 23.5094C27.0011 24.5242 27.818 25.3469 28.8258 25.3469C29.8336 25.3469 30.6506 24.5242 30.6506 23.5094C30.6506 22.4946 29.8336 21.6719 28.8258 21.6719C27.818 21.6719 27.0011 22.4946 27.0011 23.5094Z" />
            `,
    },
];

const points = [
    {
        label: 'تخصیص رفاهیات با کمترین میزان سپرده',
        icon: faGaugeMin,
    },
    {
        label: 'تسویه دوره‌ای سفرهای انجام‌شده',
        icon: faCalendarClock,
    },
    {
        label: 'مشاهده و دانلود گزارش سفرهای انجام‌شده',
        icon: faListOl,
    },
    {
        label: 'صدور صورت‌وضعیت یا فاکتور خرید کارکنان',
        icon: faReceipt,
    },
    {
        label: 'امکان تخصیص سفرکارت به شماره موبایل و کد ملی',
        icon: faUserLock,
    },
    {
        label: 'امکان استرداد اعتبار استفاده نشده',
        icon: faMoneyBillTransfer,
    },
];

const organizationalSupportNumberNormalized = computed(() =>
    organizationalSupportNumber.value === '61169999'
        ? '02161169999 داخلی 1'
        : `021${organizationalSupportNumber.value}`
);
</script>
<template>
    <div class="sections-container">
        <div class="banner">
            <div class="content">
                <div class="details">
                    <mr-typography
                        type="h1"
                        :size="{
                            desktop: 'display1',
                            mobile: 'display3',
                        }"
                        weight="700"
                        color="white"
                        class="title"
                    >
                        سفرکارت سازمانی مِستربلیط
                    </mr-typography>
                    <div class="halo">
                        <mr-typography
                            v-for="item in bannerItems"
                            :key="item"
                            size="x-large"
                            weight="500"
                            align="center"
                            color="white"
                            class="info"
                        >
                            <span class="info-icon">
                                <mr-icon
                                    :icon="faCheck"
                                    type="solid"
                                    color="var(--green-500)"
                                    size="small"
                                />
                            </span>
                            <span>{{ item }}</span>
                        </mr-typography>
                    </div>
                    <div class="details-btns">
                        <mr-button class="btn" href="#more-info" link="nuxt">
                            دریافت اطلاعات بیشتر
                        </mr-button>
                        <mr-button
                            class="support-btn"
                            variant="transparent"
                            link="nuxt"
                            :left-icon="{
                                icon: faPhone,
                                color: 'var(--yellow-400)',
                            }"
                        >
                            {{ organizationalSupportNumberNormalized }}
                        </mr-button>
                    </div>
                    <div class="pointer">
                        <mr-icon :icon="faMouse" color="white" />
                        <mr-typography color="white">
                            برای اطلاعات بیشتر به پایین صفحه بروید
                        </mr-typography>
                    </div>
                </div>
            </div>
            <div class="content-img">
                <img
                    class="cards"
                    alt="سفرکارت"
                    src="/img/gift-cards/giftcard-dual.png"
                />
            </div>
        </div>
        <section class="page-content">
            <div class="inner-content">
                <div class="header">
                    <mr-typography
                        :size="{ desktop: 'display3', mobile: 'x-large' }"
                        weight="800"
                        :align="{
                            desktop: 'right',
                            mobile: 'center',
                        }"
                    >
                        به همکاران و مشتریانتان سفر هدیه کنید!
                    </mr-typography>
                    <mr-typography
                        :size="{ desktop: 'x-large', mobile: 'small' }"
                        :align="{
                            desktop: 'right',
                            mobile: 'center',
                        }"
                        color="var(--cool-gray-600)"
                    >
                        دارندگان سفرکارت می‌توانند از تمام خدمات سفر مِستربلیط
                        به صورت آنلاین استفاده کنند.
                    </mr-typography>
                </div>
                <ul class="services">
                    <li
                        v-for="service in services"
                        :key="service.id"
                        class="service"
                    >
                        <NuxtLink class="inner-link" :to="service.href">
                            <svg
                                viewBox="0 0 40 36"
                                class="icon"
                                v-html="service.desktopIcon"
                            />
                            <span class="label">{{ service.label }}</span>
                        </NuxtLink>
                    </li>
                </ul>
                <div class="divider" />
                <div class="header">
                    <mr-typography
                        :size="{ desktop: 'display3', mobile: 'x-large' }"
                        weight="800"
                        :align="{
                            desktop: 'right',
                            mobile: 'center',
                        }"
                    >
                        بعداً حساب می‌کنیم؛ چون پیش ما اعتبار دارید.
                    </mr-typography>
                    <mr-typography
                        :size="{ desktop: 'x-large', mobile: 'small' }"
                        :align="{
                            desktop: 'right',
                            mobile: 'center',
                        }"
                        color="var(--cool-gray-600)"
                    >
                        با اعتبار ما همکاران و مشتریانتان را به سفر بفرستید
                        کارشناسان اختصاصی ما با توجه به بودجه و شرایط مدنظر شما،
                        مناسب‌ترین مدل پرداخت را پیشنهاد می‌دهند.
                    </mr-typography>
                </div>
                <ul class="points">
                    <li
                        v-for="point in points"
                        :key="point.label"
                        class="point"
                    >
                        <mr-icon
                            size="large"
                            color="var(--blue-400)"
                            :icon="point.icon"
                        />
                        <mr-typography
                            :size="{
                                desktop: 'large',
                                mobile: 'small',
                            }"
                            weight="500"
                        >
                            {{ point.label }}
                        </mr-typography>
                    </li>
                </ul>
                <div class="divider" />
                <div class="header">
                    <mr-typography
                        :size="{ desktop: 'display3', mobile: 'x-large' }"
                        type="h2"
                        weight="800"
                        :align="{
                            desktop: 'right',
                            mobile: 'center',
                        }"
                    >
                        صدور کارت سفارشی با نام و لوگوی سازمان شما
                    </mr-typography>
                    <mr-typography
                        :size="{ desktop: 'x-large', mobile: 'small' }"
                        :align="{
                            desktop: 'right',
                            mobile: 'center',
                        }"
                        color="var(--cool-gray-600)"
                        class="subtitle"
                    >
                        خریداران سفرکارت مستربلیط می‌توانند پس از ثبت درخواست،
                        کارت فیزیکی اختصاصی خود را دریافت نمایند.
                    </mr-typography>
                </div>
                <div>
                    <mr-blur-hash-image
                        class="middle-banner"
                        hash="LRN+RRWY}Nskx[s*s,Ng}#n~A2R."
                        ratio="1200:600"
                        alt="نمونه سفرکارت فیزیکی"
                        src="/img/gift-cards/giftcard-banner.png"
                    />
                </div>
                <div class="divider" />
                <div class="header">
                    <mr-typography
                        type="h2"
                        :size="{ desktop: 'display3', mobile: 'x-large' }"
                        weight="800"
                        :align="{
                            desktop: 'right',
                            mobile: 'center',
                        }"
                    >
                        سوالات پرتکرار سازمان‌ها درباره سفرکارت
                    </mr-typography>
                </div>
                <InformationContainer
                    faq-title="سوالات پرتکرار سازمان‌ها درباره سفرکارت"
                    hide-title
                    hide-busy-way
                />
            </div>
        </section>
        <section id="more-info" class="form-section">
            <div class="background" />
            <img
                src="~assets/files/giftcard-arc.svg"
                class="arc"
                alt="gift-card"
            />
            <img
                src="~assets/files/yellow-glow.svg"
                class="bottom-glow"
                alt="yellow"
            />
            <img
                src="~assets/files/blue-glow.svg"
                class="right-glow"
                alt="right"
            />
            <div class="form-content">
                <div class="form-label">
                    <mr-typography
                        type="span"
                        size="display2"
                        weight="800"
                        color="white"
                        :align="{
                            desktop: 'right',
                            mobile: 'center',
                        }"
                    >
                        به اطلاعات بیشتری نیاز دارید؟
                    </mr-typography>
                    <mr-typography
                        type="span"
                        size="x-large"
                        color="white"
                        :align="{
                            desktop: 'right',
                            mobile: 'center',
                        }"
                    >
                        فرم روبرو را تکمیل کنید یا با شماره
                        {{ organizationalSupportNumberNormalized }}
                        تماس بگیرید.
                    </mr-typography>
                </div>
                <form class="customer-form">
                    <mr-typography size="x-large" weight="800" align="center">
                        دریافت اطلاعات بیشتر
                    </mr-typography>
                    <mr-input
                        v-bind="form.fields.name.attrs"
                        placeholder="نام و نام خانوادگی"
                    />
                    <mr-input
                        v-bind="form.fields.callNumber.attrs"
                        placeholder="شماره موبایل"
                    />
                    <mr-input
                        v-bind="form.fields.organizationName.attrs"
                        placeholder="نام سازمان"
                    />
                    <mr-input
                        v-bind="form.fields.giftCardCount.attrs"
                        placeholder="تعداد سفرکارت مورد نیاز"
                        type="number"
                    />
                    <mr-input
                        v-bind="form.fields.giftCardCredit.attrs"
                        placeholder="مبلغ اعتبار سفرکارت"
                        type="money"
                    />
                    <mr-button full-width @click="addOrganization">
                        تایید اطلاعات
                    </mr-button>
                </form>
                <img
                    class="card"
                    alt="سفرکارت مستربلیط"
                    src="/img/gift-cards/giftCard.png"
                />
            </div>
        </section>
    </div>
</template>

<style scoped lang="scss">
.divider {
    border: 2px solid #f0f3f7;
}

.sections-container {
    background: white;
}

.test {
    background-color: blue;
}
.banner {
    display: flex;
    background: linear-gradient(97.64deg, #1c7cc9 11.39%, #031337 100%);
    justify-content: space-between;
    padding: 40px;
    gap: 40px;
    border-radius: 16px;
    max-width: 1200px;
    height: 480px;
    margin-inline: auto;
    @include mobile-only {
        border-radius: 0;
        height: auto;
        padding: 30px 20px;
        gap: 30px;
        flex-direction: column-reverse;
        background: linear-gradient(166.97deg, #1c7cc9 20.22%, #031337 100%);
    }

    .inside-right-glow {
        position: absolute;
        top: 0;
        right: 0;
    }
    .content {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        width: 100%;
        margin: auto;
        position: relative;
        max-width: 1100px;
        height: 100%;
        @include mobile-only {
            flex-direction: column-reverse;
            align-items: center;
            gap: 16px;
        }

        .details {
            display: flex;
            flex-direction: column;
            gap: 24px;
            @include mobile-only {
                align-items: center;
                gap: 16px;
                width: 100%;
            }
            .title {
                flex-shrink: 0;
            }
            .btn {
                width: 240px;
            }
            .details-btns {
                display: flex;
                align-items: center;
                gap: 32px;
                @include mobile-only {
                    flex-direction: column;
                    width: 100%;
                    .btn {
                        width: 100%;
                    }
                }
                .support-btn {
                    width: fit-content;
                    color: usePalette($p-yellow);
                }
            }
            .pointer {
                display: flex;
                align-items: center;
                gap: 8px;
            }
        }
        .halo {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            width: 350px;
            height: 176px;
            border-radius: 16px;
            padding: 24px;
            gap: 16px;
            background: #ffffff26;
            border-style: solid;
            border-color: linear-gradient(
                105.34deg,
                rgba(255, 255, 255, 0.25) 0%,
                rgba(255, 255, 255, 0.05) 100%
            );
            border-width: 1px 0 0 2px;
            @include mobile-only {
                gap: 10px;
                width: 100%;
            }
            .info {
                display: flex;
                gap: 20px;
                @include mobile-only {
                    gap: 10px;
                }

                .info-icon {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    width: 26px;
                    height: 26px;
                    background: #d1fae5;
                    border-radius: 2000000px;
                    @include mobile-only {
                        width: 22px;
                        height: 22px;
                    }
                }
            }
        }
    }
    .content-img {
        display: flex;
        justify-content: center;
        align-items: center;

        .cards {
            width: 400px;
            height: 400px;
            @include mobile-only {
                width: 353.16px;
                height: 304.53px;
            }
        }
    }
}

.page-content {
    background: white;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 60px 0 60px 0;
    @include mobile-only {
        padding: 20px;
    }
    .inner-content {
        display: flex;
        flex-direction: column;
        margin: auto;
        width: 100%;
        gap: 40px;
        max-width: 1200px;
        .header {
            display: flex;
            flex-direction: column;
            gap: 10px;
        }
        .services {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
            min-height: 150px;
            gap: 20px;
            padding: 0;
            margin: 0;
            @include mobile-only {
                gap: 10px;
            }
            .service {
                box-shadow: 0 1px 5px 0 #00000033;
                width: 100%;
                max-width: 181.5px;
                min-width: 150px;
                min-height: 150px;
                flex-shrink: 0;
                padding: 8px 8px 4px 8px;
                border-radius: 10px;
                @include tg-l($w7);
                @include mobile-only {
                    width: 120px;
                    min-width: 120px;
                    min-height: 100px;
                    padding: 16px 8px 16px 8px;
                    @include tg-s($w7);
                }
                .label {
                    color: usePalette($p-blue, $p-400) !important;
                }
                .icon {
                    width: 45px;
                    height: 45px;
                    fill: usePalette($p-blue, $p-400);
                    @include mobile-only {
                        width: 35px;
                        height: 35px;
                    }
                }
                .inner-link {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    height: 150px;
                    @include mobile-only {
                        height: 100px;
                    }
                }
            }
            .service:hover {
                transition-property: all;
                transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
                transition-duration: 150ms;
                background: usePalette($p-blue, $p-100);
            }
        }
        .points {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 20px;
            .point {
                display: flex;
                align-items: center;
                width: 100%;
                max-width: 386.67px;
                height: 72px;
                padding: 20px 15px 20px 15px;
                border-radius: 10px;
                gap: 10px;
                box-shadow: 0 1px 5px 0 #00000033;
                cursor: pointer;
            }
            .point:hover {
                transition-property: all;
                transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
                transition-duration: 150ms;
                background: usePalette($p-blue, $p-100);
            }
        }

        .middle-banner {
            width: 100%;
            &.desktop {
                @include mobile-only {
                    display: none;
                }
            }
            &.mobile {
                @include desktop-only {
                    display: none;
                }
            }
        }
    }
}

.form-section {
    position: relative;
    overflow: hidden;
    height: 700px;
    @include mobile-only {
        height: auto;
        padding: 30px 20px 0 20px;
    }

    .background {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background: #0f336c;
    }
    .form-content {
        width: 100%;
        height: 100%;
        position: relative;
        display: flex;
        @include mobile-only {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 30px;
        }
        .card {
            position: absolute;
            bottom: -20px;
            left: 867px;
            border-radius: 22px;
            border: 2.75px;
            z-index: 1;
            @include mobile-only {
                position: static;
                width: 100%;
                margin-bottom: -50px;
            }
        }
        .form-label {
            display: flex;
            flex-direction: column;
            gap: 20px;
            position: absolute;
            width: 600px;
            height: 103px;
            top: 147px;
            left: 740px;
            z-index: 1;
            @include mobile-only {
                position: static;
                gap: 10px;
                width: auto;
                height: auto;
            }
        }
    }
    .customer-form {
        display: flex;
        flex-direction: column;
        width: 400px;
        height: 540px;
        position: absolute;
        top: 83px;
        left: 300px;
        padding: 40px;
        border-radius: 10px;
        gap: 4px;
        box-shadow: 0 4px 15px 0 #0000001a;
        z-index: 1;
        background: white;
        @include mobile-only {
            position: static;
            padding: 20px;
            width: 100%;
            height: auto;
        }
    }
    .arc {
        position: absolute;
        top: -1px;
        width: 100%;
        transform: rotate(180deg);
        z-index: 10;
        @include mobile-only {
            display: none;
        }
    }
    .bottom-glow {
        position: absolute;
        width: 1200px;
        height: 1200px;
        top: -250px;
        left: 112.5px;
        z-index: -1px;
        @include mobile-only {
            top: -100px;
            right: 0;
        }
    }
    .right-glow {
        position: absolute;
        width: 1000px;
        height: 1000px;
        top: -400px;
        right: 0;
        transform: rotate(180deg);
        z-index: -1px;
        @include mobile-only {
            left: 100px;
            transform: rotate(90deg);
        }
    }
}
</style>
